import Badge from "@/components/Badge/Badge";
import Separator from "@/components/Separator/Separator";
import { FC, useState } from "react";
import { createSlots, Slot } from "utils/create-slots";
import Media from "../Media";
import * as AspectRatioPrimitive from "@radix-ui/react-aspect-ratio";
import Car from "@/data/car/car";
import s from "../Product.module.css";

import Collapsible from "@/components/Collapsible/Collapsible";
import { Icon } from "@/components/Icon";
import {
  formatKm,
  formatPrice,
} from "@/modules/BDK_Calculator/lib/formatFunctions";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";
import RateInfoBubble from "@/modules/RateInfoBubble/RateInfoBubble";

const ProductForm: FC<{
  item: any;
  mediaSizes?: { [key: string]: string };
  priority?: boolean;
  className?: string;
}> = ({ item, mediaSizes, priority, className = "" }) => {
  const car = Car.from(item);

  let leasingdata;
  let financedata;

  if (typeof item.leasingdata !== "undefined") {
    leasingdata =
      typeof item.leasingdata === "object"
        ? item.leasingdata
        : JSON.parse(item.leasingdata);
  } else {
    leasingdata = {};
  }
  if (typeof item.financingdata !== "undefined") {
    financedata =
      typeof item.financingdata === "object"
        ? item.financingdata
        : JSON.parse(item.financingdata);
  } else {
    leasingdata = {};
  }

  const getMonthlyRateLeasing = (obj, showNetto) => {
    if (Object.keys(obj).length === 0 && obj.constructor === Object) {
      return " Error in API - Please Check";
    } else {
      if (showNetto) {
        return `${formatPrice(
          leasingdata.response.monthlyInstallment.netValue
        )}`;
      } else {
        return `${formatPrice(
          leasingdata.response.monthlyInstallment.grossValue
        )}`;
      }
    }
  };
  const getMonthlyRateFinancing = (obj, showNetto) => {
    if (Object.keys(obj).length === 0 && obj.constructor === Object) {
      return " Error in API - Please Check";
    } else {
      if (showNetto) {
        return `${formatPrice(
          financedata.response.monthlyInstallmentTotalAmount.netValue
        )}`;
      } else {
        return `${formatPrice(
          financedata.response.monthlyInstallmentTotalAmount.grossValue
        )}`;
      }
    }
  };

  if (!car) return null;

  return (
    <div>
      <CalculatorDataHeader />
      <div
        className={`shadow-md w-full lg:max-w-[355px] lg:min-h-[100px] min-h-[95px] bg-white ${s.Product} h-full flex flex-col ${className} `}
      >
        <div className={"lg:block hidden"}>
          <AspectRatioPrimitive.Root ratio={4 / 3}>
            <>
              <Media
                alt={`${car?.title} ${car?.farbe ? `in ${car?.farbe}` : ""}`}
                content={car.assets}
                sizesObj={mediaSizes}
                priority={priority}
                hasVideo={false}
              />
              <div className="absolute top-4 left-5 min-h-[100px]">
                {car.rabatt > 0 && (
                  <Badge color="discount" size="small">
                    -{car.rabatt}% Rabatt*
                  </Badge>
                )}
              </div>
            </>
          </AspectRatioPrimitive.Root>
        </div>
        <div className=" px-5 pb-3 pt-4 h-full">
          <Content carItem={item}>
            <Slot name="title">{car?.title} </Slot>
            <Slot name="subtitle">{car?.title_sub}</Slot>
            {car?.formattedPrice && (
              <Slot name="price">
                {car.formattedPrice}
                {car.showNettoPreis && (
                  <span className="text-xs ml-2">zzgl. MwSt.</span>
                )}
              </Slot>
            )}

            {car?.rabatt && <Slot name="discount">-{car.rabatt}% Rabatt</Slot>}
            <Slot name="features">
              <li>
                <span>Kilometerstand: </span>
                {`${car.kilometer.toLocaleString("de-DE")} km`}
              </li>
              <li>
                <span>Farbe: </span>
                {car?.farbe}
              </li>
              <li
                className={`${
                  car.ezTeaser === "Tageszulassung" ||
                  car.ezTeaser === "Neuwagen"
                    ? "font-bold"
                    : ""
                }`}
              >
                <span>{`${
                  car.ezTeaser === "Tageszulassung" ||
                  car.ezTeaser === "Neuwagen"
                    ? ""
                    : "Erstzulassung: "
                }`}</span>
                {car?.ezTeaser}
              </li>
              <li>
                <span>Türen: </span>
                {car?.tueren}
              </li>
              <li>
                <span>Getriebe: </span>
                {car?.getriebeart}
              </li>
              <li>
                <span>Sitze: </span>
                {car?.sitze}
              </li>
              <li>
                <span>Kraftstoff: </span>
                {car?.kraftstoffart}
              </li>
              <li>
                <span>Hubraum: </span>
                {`${car.ccm.toLocaleString("de-DE")} cm³`}
              </li>
              <li>
                <span>Leistung: </span>
                {car?.ps || car?.leistung} PS
              </li>
              <li>
                <span>Marke: </span>
                {car?.marke}
              </li>
            </Slot>
            <Slot name="finePrint">{car?.consumptionAndCo2Emission}</Slot>
          </Content>
        </div>
      </div>
    </div>
  );
};

const Content = ({ children, carItem }) => {
  const [data, setData] = useState<any>({});
  const [rate, setRate] = useState<string | number>("");
  const [calcData, setCalcData] = useState<string | number>("");
  const regex = /^\d/;
  const getMonthlyRateFinancing = (obj, showNetto) => {
    if (Object.keys(obj).length === 0 && obj.constructor === Object) {
      return " Error in API - Please Check";
    } else {
      if (showNetto) {
        return `${formatPrice(
          obj.financingdata.response.monthlyInstallmentTotalAmount.netValue
        )}`;
      } else {
        return `${formatPrice(
          obj.financingdata.response.monthlyInstallmentTotalAmount.grossValue
        )}`;
      }
    }
  };

  useIsomorphicLayoutEffect(() => {
    let localData;
    let localRate;

    localData = localStorage.getItem("calculatorData") || "";
    localRate = localStorage.getItem("calculatorDataRate") || "";
    localData && setData(JSON.parse(localData));
    setRate(localRate);
    setCalcData(localData);
  }, []);
  const [title, subtitle, price, monthlyPrice, discount, features, finePrint] =
    createSlots(
      [
        "title",
        "subtitle",
        "price",
        "monthlyPrice",
        "discount",
        "features",
        "finePrint",
      ],
      children
    );

  return (
    <div className="h-full flex flex-col">
      <div className="my-3">
        <div className="flex w-full justify-between">
          <p className="text-sm truncate max-w-[260px] font-bold">{title}</p>
          <div className={`md:flex hidden items-center shrink-0 `}>
            <Badge color="success" size="small">
              auf Lager
            </Badge>
          </div>
        </div>
        <p className="text-xs font-normal text-blue-dark truncate lg:max-w-[160px] max-w-[310px] lg:mt-0 mt-3">
          {subtitle}
        </p>
      </div>
      <div
        className={`flex justify-between text-blue-dark mb-auto lg:mt-0 mt-3 pb-5 lg:pb-0 ${s.ProductPrices}`}
      >
        {price && (
          <div className="font-bold flex justify-between items-center lg:mb-0 mb-4">
            <span className="text-xs lg:mr-3 md:mb-0 mb-[-3px] mr-3">
              Sofortkaufpreis
            </span>
            <span className="text-sm  text-alert">{price}</span>
          </div>
        )}
        {rate === "NaN €" && carItem && (
          <div className="font-bold flex justify-between items-center">
            <div>
              <span className="text-xs lg:mr-3 md:mb-0 mb-[-3px] mr-3">
                mtl. finanzieren**
              </span>
            </div>
            <div className=" text-alert flex-row flex items-baseline">
              <span className="text-sm">
                {getMonthlyRateFinancing(carItem, carItem.showNettoPreis)}
              </span>
              {carItem.showNettoPreis && (
                <span className="text-xs  md:mb-0 mb-[-3px] ml-3">
                  zzgl. MwSt.
                </span>
              )}
              {carItem.financingdata && (
                <RateInfoBubble
                  financedata={carItem.financingdata}
                  iconVariant="Product"
                />
              )}
            </div>
          </div>
        )}

        {/* {monthlyPrice && !rate && (
          <div className="font-bold flex justify-between items-center ">
            <span className="text-xs lg:mr-3 md:mb-0 mb-[-3px] mr-3">
              mtl. leasen**
            </span>
            <span className="text-sm  text-alert">{monthlyPrice}</span>
          </div>
        )}
        {rate && regex.test(rate.toString()) && (
          <div className="font-bold flex justify-between items-center">
            <div>
              <span className="text-xs lg:mr-3 md:mb-0 mb-[-3px] mr-3">
                mtl. finanzieren**
              </span>
            </div>
            <div className=" text-alert">
              <span className="text-sm">{rate}</span>
              {data.customerType === "business" && (
                <span className="text-xs  md:mb-0 mb-[-3px] ml-3">
                  zzgl. MwSt.
                </span>
              )}
            </div>
          </div>
        )} */}
      </div>
      <div className={`flex md:hidden items-center shrink-0 mt-4 `}>
        <Badge color="success" size="small" className="w-full">
          auf Lager
        </Badge>
        {discount && (
          <Badge color="discount" size="small" className="ml-4 w-full">
            {discount}*
          </Badge>
        )}
      </div>
      <Separator className="!bg-gray-300 mt-4 block " />
      <ul className="text-xs grid  grid-cols-2 gap-4  my-6  list-disc list-inside [&>li>span]:font-bold [&>li>span]:ml-[-8px] ">
        {features}
      </ul>

      <CalculatorData data={data} rate={rate} calculatorData={calcData} />
    </div>
  );
};

const CalculatorData = ({ data, rate, calculatorData }) => {
  const [open, setOpen] = useState(true);
  const regex = /^\d/;
  const rateInfoBubbleData = { response: data, request: data };

  if (!data || !rate || !calculatorData) return null;

  return (
    <div>
      <Collapsible
        onOpenChange={setOpen}
        open={open}
        variant="default"
        className="flex flex-col "
      >
        <Slot name="trigger">
          <Collapsible.Trigger
            className={`select-none flex font-bold border-t-[1px] border-gray-300 text-blue-dark hover:text-blue-medium  py-4 w-full order-2  items-center align-middle justify-center`}
          >
            {data?.type === "financing"
              ? "Ballonkredit Details"
              : "Leasing Details"}
            <Icon
              type="chevron"
              className={`${
                open ? "-rotate-90" : "rotate-90"
              } w-6 h-6 text-blue-dark ml-4`}
            />
          </Collapsible.Trigger>
        </Slot>
        <Slot name="content">
          <div className="text-xs w-full  border-t-[1px] border-gray-300 pt-5 pb-6 ">
            <table className="table-auto w-full leading-[1.8]">
              <tbody>
                <tr>
                  <td className="w-[53%] ">Monatliche Darlehensrate:</td>
                  <td className="font-bold fley flex-row">
                    {regex.test(rate.toString()) ? rate : "auf Anfrage"}{" "}
                    {regex.test(rate.toString()) &&
                    data.customerType === "business"
                      ? " zzgl. MwSt."
                      : ""}
                    {rateInfoBubbleData && (
                      <RateInfoBubble
                        financedata={rateInfoBubbleData}
                        showNettoPreis={true}
                        iconVariant={"ProductForm"}
                      />
                    )}
                  </td>
                </tr>
                {data.deposit >= 0 && (
                  <tr>
                    <td>
                      {data.type === "leasing" ? "Sonderzahlung:" : "Anzahlung"}
                    </td>
                    <td className="font-bold">
                      {formatPrice(data?.deposit) || "Deposit"}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>Laufzeit:</td>
                  <td className="font-bold">{data?.term || "Term"} Monate</td>
                </tr>

                <tr>
                  <td>Anzahl der Raten:</td>
                  <td className="font-bold">{data?.term || "Term"}</td>
                </tr>
                <tr>
                  <td>Effektiver Jahreszins:</td>
                  <td className="font-bold">
                    {`${data.effectiveInterestRate} %` || "3.99 %"}
                  </td>
                </tr>

                {data?.type !== "financing" &&
                  data.contractualMileage !== 0 && (
                    <tr>
                      <td>Jahreslaufleistung:</td>
                      <td className="font-bold">
                        {formatKm(data?.contractualMileage) || "n.a."}
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
        </Slot>
      </Collapsible>
    </div>
  );
};
const CalculatorDataHeader = () => {
  const [data, setData] = useState<any>({});

  useIsomorphicLayoutEffect(() => {
    let localData;

    localData = localStorage.getItem("calculatorData") || "";

    localData && setData(JSON.parse(localData));
  }, []);

  return (
    <div className="w-full">
      <p className="font-bold text-2xl mb-6">
        Unser {data?.type === "financing" && "Ballonkredit-"}
        {data?.type === "leasing" && "Leasing-"}
        Angebot für Sie:
      </p>
    </div>
  );
};

export default Object.assign(ProductForm, {
  Media,
  Content,
  CalculatorData,
});
